import React from 'react';
import { formValueSelector, getFormMeta } from 'redux-form';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { ImagePreview } from 'onefaceui-react-components';
import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import {
  TextField,
  LookupField,
  CheckboxField,
  IntegerField,
  DecimalField,
  GroupField
} from 'react/components/fields';
import actions from 'react/api/actions';
import { getImagePreview } from 'react/api/selectors/sku';
import { validIfServerVersionIsBiggerThan } from 'react/utils/utils';

const skuActions = actions.sku.actionCreators;

const getHintsecondaryQty = (description) => (
  (description) ? Monet.I18n.getResourcesByKey('sku.secondaryQty.message', description) : 'sku.secondaryQty.fallback'
);

const getHintSecondaryCompound = (description, secondaryQty) => (
  (description && secondaryQty) ? Monet.I18n.translateResources(
    'sku.secondaryCompound.message',
    {
      skuQuantity: secondaryQty,
      skuDescription: description
    }
  ) : 'sku.secondaryCompound.fallback'
);

const getHintSecondaryCompoundQty = (description, secondaryQty, secondaryCompound) => (
  (description
    && secondaryQty
    && secondaryCompound?.description) ? Monet.I18n.translateResources(
      'sku.secondaryCompoundQty.message',
      {
        secondaryCompoundSku: secondaryCompound.description,
        skuQuantity: secondaryQty,
        skuDescription: description
      }
    ) : 'sku.secondaryCompoundQty.fallback'
);

const getHintTertiaryQty = (secondaryCompound) => (
  (secondaryCompound?.description) ? Monet.I18n.getResourcesByKey('sku.tertiaryQty.message', secondaryCompound.description) : 'sku.tertiaryQty.fallback'
);

const getHintTertiaryCompound = (tertiaryQty, secondaryCompound = {}) => (
  (tertiaryQty && secondaryCompound?.description)
    ? Monet.I18n.translateResources(
      'sku.tertiaryCompound.message',
      {
        secondaryBundleQuantity: tertiaryQty,
        secondaryCompoundSku: secondaryCompound.description
      }
    ) : 'sku.tertiaryCompound.fallback'
);

const getHintTertiaryCompoundQty = (tertiaryQty, secondaryCompound = {}, tertiaryCompound = {}) => (
  (tertiaryQty && secondaryCompound?.description && tertiaryCompound?.description)
    ? Monet.I18n.translateResources(
      'sku.tertiaryCompoundQty.message',
      {
        tertiaryCompoundSku: tertiaryCompound.description,
        secondaryBundleQuantity: tertiaryQty,
        secondaryCompoundSku: secondaryCompound.description
      }
    ) : 'sku.tertiaryCompoundQty.fallback'
);

const renderGeneralSection = ({ imagePreview, formMeta }, dispatch) => {
  const imageURLMeta = formMeta?.skuImageUrl || {};
  return (
  <FormSection title="sku.general.title">
    <FormSectionColumn>
      <TextField
        label="sku.key"
        name="key"
        disabledOnEdit
        required
      />
      <LookupField
        label="sku.organization"
        name="organization"
        lookupEntity="Organization"
        lookupEntityColumn="description"
        disabledOnEdit
        required
      />
      <TextField
        label="sku.description"
        name="description"
        required
      />
      <CheckboxField
        label="sku.enabled"
        name="enabled"
      />
    </FormSectionColumn>
    <FormSectionColumn>
      <TextField
        label="sku.brand"
        name="brand"
      />
      <LookupField
        label="sku.skuType"
        name="skuType"
        lookupEntity="SkuType"
        lookupEntityColumn="description"
      />
      <LookupField
        label="sku.skuGroup"
        name="skuGroup"
        lookupEntity="SkuGroup"
        lookupEntityColumn="description"
      />
      {validIfServerVersionIsBiggerThan(6.49) && <TextField
        label="sku.field.imageURL"
        name="skuImageUrl"
        meta={{ ...imageURLMeta, error: imagePreview.get('hasError') }}
        onBlur={async ({ target: { value } }) => {
          if (!value) {
            dispatch(skuActions.resetImagePreview());
            return;
          }
          if (value !== imagePreview.get('imageURL')) {
            dispatch(skuActions.updateImagePreviewComponent({
              errorMessage: '',
              hasError: false,
              showLoading: true
            }));
            dispatch(skuActions.requestValidateSkuImageUrl({ imageUrl: value }));
          }
        }}
      />}
    </FormSectionColumn>
    {validIfServerVersionIsBiggerThan(6.49) && <FormSectionColumn>
      <ImagePreview
        imageURL={imagePreview.get('imageURL', '')}
        labelText={Monet.I18n.getResourcesByKey('sku.imageURLPreview')}
        imageContainerWidth={100}
        imageContainerHeight={120}
        errorMessage={imagePreview.get('errorMessage', '')}
        hasError={imagePreview.get('hasError')}
        showLoading={imagePreview.get('showLoading')}
        onLoad={() => {
          dispatch(skuActions.updateImagePreviewComponent({
            showLoading: false
          }));
        }}
        onError={() => {
          dispatch(skuActions.updateImagePreviewComponent({
            showLoading: false,
            hasError: true,
            errorMessage: Monet.I18n.getResourcesByKey('sku.imageURLPreview.error')
          }));
        }}
      />
    </FormSectionColumn>}
  </FormSection>
)};

const renderSkuUnitsConfigurationSection = () => (
  <FormSection title="sku.unitsConfiguration.title">
    <FormSectionColumn>
      <LookupField
        label="sku.baseUnit"
        name="baseUnit"
        lookupEntity="SizeAlias"
        lookupEntityColumn="description"
      />
      <LookupField
        label="sku.secondaryUnit"
        name="secondaryUnit"
        lookupEntity="SizeAlias"
        lookupEntityColumn="description"
      />
      <LookupField
        label="sku.tertiaryUnit"
        name="tertiaryUnit"
        lookupEntity="SizeAlias"
        lookupEntityColumn="description"
      />
    </FormSectionColumn>
  </FormSection>
);

function renderFirstCompoundSkuConfigurationColumn({
  hasSecondaryBundle, description, secondaryQty, secondaryCompound
}) {
  return (
    <FormSectionColumn>
      <CheckboxField
        label="sku.hasSecondaryBundle"
        name="hasSecondaryBundle"
      />
      <IntegerField
        label="sku.secondaryQty"
        name="secondaryQty"
        min={1}
        focusHint={getHintsecondaryQty(description)}
        shouldShow={Boolean(hasSecondaryBundle)}
        required
      />
      <LookupField
        label="sku.secondaryCompound"
        name="secondaryCompound"
        lookupEntity="Sku"
        lookupEntityColumn="description"
        focusHint={getHintSecondaryCompound(description, secondaryQty)}
        shouldShow={Boolean(hasSecondaryBundle)}
        required
      />
      <IntegerField
        label="sku.secondaryCompoundQty"
        name="secondaryCompoundQty"
        min={1}
        focusHint={getHintSecondaryCompoundQty(description, secondaryQty, secondaryCompound)}
        shouldShow={Boolean(hasSecondaryBundle)}
        required
      />
    </FormSectionColumn>
  );
}

function renderSecondCompoundSkuConfigurationColumn({
  hasSecondaryBundle, hasTertiaryBundle, tertiaryQty, secondaryCompound, tertiaryCompound
}) {
  return (
    <FormSectionColumn>
      <CheckboxField
        label="sku.hasTertiaryBundle"
        name="hasTertiaryBundle"
        disabled={!hasSecondaryBundle}
        value={!hasSecondaryBundle ? false : undefined}
      />
      <IntegerField
        label="sku.tertiaryQty"
        name="tertiaryQty"
        min={1}
        focusHint={getHintTertiaryQty(secondaryCompound)}
        shouldShow={Boolean(hasTertiaryBundle)}
        required
      />
      <LookupField
        label="sku.tertiaryCompound"
        name="tertiaryCompound"
        lookupEntity="Sku"
        lookupEntityColumn="description"
        focusHint={getHintTertiaryCompound(tertiaryQty, secondaryCompound)}
        shouldShow={Boolean(hasTertiaryBundle)}
        required
      />
      <IntegerField
        label="sku.tertiaryCompoundQty"
        name="tertiaryCompoundQty"
        min={1}
        focusHint={getHintTertiaryCompoundQty(tertiaryQty, secondaryCompound, tertiaryCompound)}
        shouldShow={Boolean(hasTertiaryBundle)}
        required
      />
    </FormSectionColumn>
  );
}

const renderCompoundSkuConfigurationSection = (props) => (
  <FormSection title="sku.compoundConfiguration.title">
    {renderFirstCompoundSkuConfigurationColumn(props)}
    {renderSecondCompoundSkuConfigurationColumn(props)}
  </FormSection>
);

const renderSkuSizeConversionSection = () => (
  <FormSection title="sku.retornableSKUs.title" subtitle="SkuSizeConversion.section.retornable.subtitle">
    <GroupField name="skuSizeConversions">
      <LookupField
        label="retornableSKUs.parentSkuSizeAlias"
        name="parentSkuSizeAlias"
        lookupEntity="SizeAlias"
        lookupEntityColumn="description"
        required
      />
      <LookupField
        label="retornableSKUs.retornableSKU"
        name="skuChild"
        lookupEntity="Sku"
        lookupEntityColumn="description"
        required
      />
      <LookupField
        label="skuSizeConversion.sizeAlias"
        name="sizeAlias"
        lookupEntity="SizeAlias"
        lookupEntityColumn="description"
        required
      />
      <DecimalField
        label="retornableSKUs.qty"
        name="qty"
        required
      />
    </GroupField>
  </FormSection>
);

const beforeSubmit = (imagePreview) => (values) => {
  if (imagePreview.get('hasError')) {
    return false;
  }

  const { skuSizeConversions } = values;
  const adjustedValues = values;

  if (skuSizeConversions) {
    const skuSizeConversionsAdjusted = skuSizeConversions.map(({ id, ...attrs }) => attrs);
    adjustedValues.skuSizeConversions = skuSizeConversionsAdjusted;
  }

  return adjustedValues;
};

function SkuCrud(props) {
  const dispatch = useDispatch();
  const { imagePreview } = props;
  return (
    <BaseCrud
      tabName="sku.gridIndex"
      beforeSubmit={beforeSubmit(imagePreview)}
      updateMethod="POST"
      isLoading={imagePreview.get('showLoading')}
      {...props}
    >
      {renderGeneralSection(props, dispatch)}
      {renderSkuUnitsConfigurationSection()}
      {renderCompoundSkuConfigurationSection(props)}
      {renderSkuSizeConversionSection()}
    </BaseCrud>
  );
}

renderFirstCompoundSkuConfigurationColumn.propTypes = {
  hasSecondaryBundle: PropTypes.bool.isRequired,
  description: PropTypes.string,
  secondaryQty: PropTypes.number,
  secondaryCompound: PropTypes.object
};

renderFirstCompoundSkuConfigurationColumn.defaultProps = {
  description: null,
  secondaryQty: null,
  secondaryCompound: null
};

renderSecondCompoundSkuConfigurationColumn.propTypes = {
  hasSecondaryBundle: PropTypes.bool.isRequired,
  hasTertiaryBundle: PropTypes.bool.isRequired,
  tertiaryQty: PropTypes.number,
  secondaryCompound: PropTypes.object,
  tertiaryCompound: PropTypes.object
};

renderSecondCompoundSkuConfigurationColumn.defaultProps = {
  tertiaryQty: null,
  secondaryCompound: null,
  tertiaryCompound: null
};

SkuCrud.propTypes = {
  imagePreview: PropTypes.object
};

SkuCrud.defaultProps = {
  imagePreview: null
};

const selector = formValueSelector('Sku');
const SkuWrapper = connect(
  (state) => ({
    hasSecondaryBundle: selector(state, 'hasSecondaryBundle'),
    hasTertiaryBundle: selector(state, 'hasTertiaryBundle'),
    description: selector(state, 'description'),
    secondaryQty: selector(state, 'secondaryQty'),
    tertiaryQty: selector(state, 'tertiaryQty'),
    secondaryCompound: selector(state, 'secondaryCompound'),
    tertiaryCompound: selector(state, 'tertiaryCompound'),
    skuImageUrl: selector(state, 'skuImageUrl'),
    imagePreview: getImagePreview(state),
    formMeta: getFormMeta('Sku')(state)
  })
)(SkuCrud);

export default SkuWrapper;
